import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { slotRepository } from "./slot.repository";
import { Slot } from "./types/slot.entity";
import { Timestamp } from "firebase/firestore";

export type SlotsFilter = Partial<Pick<Slot, "userId" | "status" | "coachType" | "langCode">> & {
  slotId?: string;
  date?: { $gte?: Timestamp; $lte?: Timestamp };
};

const slotsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getSlots: builder.query<(Slot & { id: string })[], { where?: SlotsFilter; offset?: string; limit?: number }>({
      queryFn: async ({ where = {}, offset, limit }) => {
        // Helper to check if a slot matches non-id filters
        const matchesFilters = (s: Slot, filters: Partial<SlotsFilter>): boolean => {
          const { slotId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => (value == null ? true : (s as any)[key] === value));
        };

        if (where.slotId) {
          const { slotId, ...restFilters } = where;
          let slot: (Slot & { id: string }) | null = null;
          try {
            slot = (await slotRepository.findById(slotId as string)) as (Slot & { id: string }) | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch slot" } as any;
          }
          if (slot && matchesFilters(slot, restFilters)) return { data: [slot] } as any;
          // Not found or does not match other filters: empty dataset
          return { data: [] } as any;
        }

        const res = await executeQuery(() =>
          slotRepository.find({ where, offset, limit, orderBy: ["createdAt", "desc"] })
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as (Slot & { id: string })[] };
      },
    }),

    getSlotById: builder.query<Slot & { id: string }, string>({
      queryFn: async (id) => {
        const res = await executeQuery(() => slotRepository.findById(id));
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as Slot & { id: string } };
      },
    }),

    countSlots: builder.query<number, { where?: SlotsFilter }>({
      queryFn: async ({ where = {} }) => {
        // Helper to check if a slot matches non-id filters
        const matchesFilters = (s: Slot, filters: Partial<SlotsFilter>): boolean => {
          const { slotId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => (value == null ? true : (s as any)[key] === value));
        };

        if (where.slotId) {
          const { slotId, ...restFilters } = where;
          let slot: Slot | null = null;
          try {
            slot = (await slotRepository.findById(slotId as string)) as Slot | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch slot" } as any;
          }
          // Return 1 only if the slot exists and matches other filters; else 0
          return { data: slot && matchesFilters(slot, restFilters) ? 1 : 0 } as any;
        }
        return executeQuery(() => slotRepository.count(where as any));
      },
    }),
  }),
});

export const { useGetSlotsQuery, useCountSlotsQuery, useGetSlotByIdQuery } = slotsService;


